import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import MessagingService from '../services/messagingService';

const ClientMessagingContext = createContext();

export const useClientMessaging = () => {
  const context = useContext(ClientMessagingContext);
  if (!context) {
    throw new Error('useClientMessaging must be used within a ClientMessagingProvider');
  }
  return context;
};

export const ClientMessagingProvider = ({ children, db, client }) => {
  const [messagingService, setMessagingService] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({ internal: 0, client: 0, group: 0 });
  const [loading, setLoading] = useState(true);

  // Initialiser le service de messagerie
  useEffect(() => {
    if (db) {
      const service = new MessagingService(db);
      setMessagingService(service);
    }
  }, [db]);

  // Écouter les conversations du client
  useEffect(() => {
    if (!messagingService || !client?.id) return;

    setLoading(true);
    
    const unsubscribe = messagingService.listenToUserConversations(
      client.id,
      (conversationsList) => {
        setConversations(conversationsList);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [messagingService, client?.id]);

  // Écouter les messages de la conversation active
  useEffect(() => {
    if (!messagingService || !activeConversation) {
      console.log('[ClientMessagingContext] ⚠️ Listener non activé:', { messagingService: !!messagingService, activeConversation: !!activeConversation });
      return;
    }

    console.log('[ClientMessagingContext] 🎬 Activation du listener pour:', activeConversation.id);

    const unsubscribe = messagingService.listenToMessages(
      activeConversation.id,
      (messagesList) => {
        console.log('[ClientMessagingContext] 📬 Messages reçus dans le context:', messagesList.length);
        setMessages(messagesList);

        // Marquer comme lus quand on ouvre la conversation
        if (client?.id) {
          messagingService.markConversationAsRead(activeConversation.id, client.id);
        }
      }
    );

    return () => {
      console.log('[ClientMessagingContext] 🔌 Déconnexion du listener pour:', activeConversation.id);
      unsubscribe();
    };
  }, [messagingService, activeConversation?.id, client?.id]);

  // Mettre à jour les compteurs de messages non lus
  const updateUnreadCounts = useCallback(async () => {
    if (!messagingService || !client?.id) return;

    try {
      const counts = await messagingService.getUnreadCounts(client.id);
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Erreur mise à jour compteurs non lus:', error);
    }
  }, [messagingService, client?.id]);

  // Mettre à jour les compteurs périodiquement
  useEffect(() => {
    if (!messagingService || !client?.id) return;

    // Mettre à jour immédiatement
    updateUnreadCounts();

    // Mettre à jour toutes les 30 secondes
    const interval = setInterval(updateUnreadCounts, 30000);

    return () => clearInterval(interval);
  }, [messagingService, client?.id, updateUnreadCounts]);

  // Mettre à jour les compteurs quand les conversations changent
  useEffect(() => {
    updateUnreadCounts();
  }, [conversations, updateUnreadCounts]);

  // Fonction pour envoyer un message
  const sendMessage = async (content) => {
    if (!messagingService || !activeConversation || !client) {
      throw new Error('Service, conversation ou client manquant');
    }

    const senderName = `${client.firstName} ${client.lastName || ''}`.trim();
    return await messagingService.sendMessage(
      activeConversation.id,
      client.id,
      senderName,
      content
    );
  };

  // Fonction pour démarrer une nouvelle conversation
  const startConversation = async (otherUserId, conversationType = 'client') => {
    if (!messagingService || !client?.id) {
      throw new Error('Service ou client manquant');
    }

    const conversationId = await messagingService.findOrCreateConversation(
      client.id,
      otherUserId,
      conversationType
    );

    // Créer une conversation temporaire pour l'affichage immédiat
    const tempConversation = {
      id: conversationId,
      participants: [client.id, otherUserId],
      type: conversationType,
      lastMessage: '',
      lastMessageTimestamp: new Date(),
      unreadCount: 0,
      archived: false,
      // Ajouter des propriétés pour l'affichage
      participantNames: 'Chargement...',
      isTemp: true // Marquer comme temporaire
    };

    // Ajouter immédiatement à la liste des conversations
    setConversations(prev => {
      // Vérifier si la conversation existe déjà
      const exists = prev.find(conv => conv.id === conversationId);
      if (exists) return prev;
      
      return [tempConversation, ...prev];
    });

    setActiveConversation(tempConversation);
    return conversationId;
  };

  // Fonction pour archiver une conversation
  const archiveConversation = async (conversationId) => {
    if (!messagingService || !client?.id) return;

    await messagingService.archiveConversation(conversationId, client.id);
    
    // Si c'est la conversation active, la désactiver
    if (activeConversation?.id === conversationId) {
      setActiveConversation(null);
      setMessages([]);
    }
  };

  // Fonction pour désarchiver une conversation
  const unarchiveConversation = async (conversationId) => {
    if (!messagingService) return;
    await messagingService.unarchiveConversation(conversationId);
  };

  // Fonction pour marquer une conversation comme lue
  const markAsRead = async (conversationId) => {
    if (!messagingService || !client?.id) return;
    await messagingService.markConversationAsRead(conversationId, client.id);
  };

  // Filtrer les conversations par type et statut d'archivage
  const getFilteredConversations = (type = 'client', showArchived = false) => {
    return conversations.filter(conv =>
      conv.type === type &&
      conv.archived === showArchived
    );
  };

  const value = {
    // État
    conversations,
    activeConversation,
    messages,
    unreadCounts,
    loading,
    client,

    // Actions
    setActiveConversation,
    sendMessage,
    startConversation,
    archiveConversation,
    unarchiveConversation,
    markAsRead,
    getFilteredConversations,
    updateUnreadCounts,

    // Service
    messagingService
  };

  return (
    <ClientMessagingContext.Provider value={value}>
      {children}
    </ClientMessagingContext.Provider>
  );
};

export default ClientMessagingContext;
